import type { AccountResponse, Passkey } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquareText, Monitor } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { currentLocale, LOCALES } from "../i18n/i18n.ts";
import { useMe } from "../me.ts";
import { MemorySection } from "../memory/MemorySection.tsx";
import { Link, MY_FEEDBACK_PATH } from "../router.tsx";
import { PasskeysPanel, passkeyName } from "../security/PasskeysPanel.tsx";
import { usePasskeyAdd } from "../security/passkey-add.ts";
import { type Secure, useSecureAction } from "../security/step-up.tsx";
import { useSession } from "../session.tsx";
import { PageSection, SubHeading } from "../settings-shell/PageSection.tsx";
import { createPasskey } from "../shell/webauthn.ts";
import { type ThemeChoice, useTheme } from "../theme/theme.tsx";
import { ConnectedApps } from "./ConnectedApps.tsx";
import { EsiCredentialSection, showsEsiCredential } from "./EsiCredentialSection.tsx";
import { McpServer } from "./McpServer.tsx";
import { NewPasswordField, passwordLongEnough } from "./PasswordField.tsx";
import { useSavePreferences } from "./preferences.ts";
import { accountKeys, csrf, done, useAccount, useAccountSessions } from "./queries.ts";
import { StepUpDialog } from "./StepUpDialog.tsx";
import { TotpSetup } from "./TotpSetup.tsx";

const THEMES: ThemeChoice[] = ["system", "light", "dark"];

/** The message of a failed account action, in plain words. */
function failure(error: unknown, t: (key: "errors.network") => string): string {
  return error instanceof ApiError ? error.message : t("errors.network");
}

/**
 * The signed-in user's account: profile, preferences, sign-in security and sessions. Federated
 * users see their organisation's sign-in in charge of password and second factors.
 */
export function AccountSettings() {
  const { t } = useTranslation();
  const account = useAccount();
  const queryClient = useQueryClient();
  const stepUp = useSecureAction();
  const { secure } = stepUp;

  return (
    <Page width="narrow" className="gap-6">
      <PageHeader
        title={t("accountSettings.title")}
        lead={t("accountSettings.lead")}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link href={MY_FEEDBACK_PATH}>
              <MessageSquareText aria-hidden="true" />
              {t("accountSettings.myFeedback")}
            </Link>
          </Button>
        }
      />
      {account.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : account.isError ? (
        <Notice tone="danger">{failure(account.error, t)}</Notice>
      ) : (
        <div className="flex min-w-0 flex-col divide-y divide-line">
          <PageSection id="profile" title={t("accountSettings.sections.profile")}>
            <ProfileSection account={account.data} />
          </PageSection>
          <PageSection id="preferences" title={t("accountSettings.sections.preferences")}>
            <PreferencesSection />
          </PageSection>
          <PageSection id="memory" title={t("accountSettings.sections.memory")}>
            <MemorySection />
          </PageSection>
          <PageSection id="security" title={t("accountSettings.sections.security")}>
            {account.data.federated ? (
              <div className="flex flex-col gap-8">
                <Notice>
                  {showsEsiCredential(account.data)
                    ? t("accountSettings.federatedSecurityBeside")
                    : t("accountSettings.federatedSecurity")}
                </Notice>
                {showsEsiCredential(account.data) ? (
                  <EsiCredentialSection account={account.data} secure={secure} />
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                <PasswordSection />
                <TotpSection account={account.data} secure={secure} />
                {account.data.security.passkeysAvailable ? (
                  <PasskeysSection account={account.data} secure={secure} />
                ) : null}
                {showsEsiCredential(account.data) ? (
                  <EsiCredentialSection account={account.data} secure={secure} />
                ) : null}
              </div>
            )}
          </PageSection>
          <PageSection id="sessions" title={t("accountSettings.sections.sessions")}>
            <SessionsSection secure={secure} />
          </PageSection>
          <PageSection id="connected-apps" title={t("accountSettings.sections.connectedApps")}>
            <ConnectedAppsSection />
          </PageSection>
          <PageSection id="mcp" title={t("accountSettings.sections.mcp")}>
            <McpServer />
          </PageSection>
        </div>
      )}
      <StepUpDialog
        open={stepUp.asking}
        passkeys={account.data?.security.passkeysAvailable ?? false}
        federatedEmail={account.data?.federated ? account.data.email : null}
        onClose={stepUp.cancel}
        onDone={() => {
          const action = stepUp.take();
          void queryClient.invalidateQueries({ queryKey: accountKeys.account });
          if (action) {
            void secure(action).catch(() => undefined);
          }
        }}
      />
    </Page>
  );
}

function ProfileSection({ account }: { account: AccountResponse }) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(account.displayName ?? "");
  const save = useMutation({
    mutationFn: async (displayName: string) =>
      done(await api.api.account.profile.$patch({ json: { displayName } }, csrf(csrfToken))),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: accountKeys.account });
    },
  });
  if (account.federated) {
    return (
      <dl
        data-esi-content="people"
        className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]"
      >
        <dt className="text-fg-muted">{t("accountSettings.profile.name")}</dt>
        <dd className="flex flex-col gap-0.5">
          <span className="text-fg">{account.displayName ?? "—"}</span>
          <span className="text-xs text-fg-muted">{t("accountSettings.profile.fromIdp")}</span>
        </dd>
        <dt className="text-fg-muted">{t("accountSettings.profile.email")}</dt>
        <dd className="text-fg">{account.email}</dd>
      </dl>
    );
  }
  const trimmed = name.trim();
  return (
    <form
      data-esi-content="people"
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed && trimmed !== account.displayName) {
          save.mutate(trimmed);
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("accountSettings.profile.name")}</Label>
        <Input
          id={`${id}-name`}
          required
          maxLength={100}
          autoComplete="name"
          value={name}
          onChange={(e) => {
            save.reset();
            setName(e.currentTarget.value);
          }}
        />
      </div>
      <p className="text-sm text-fg-secondary">
        <span className="text-fg-muted">{t("accountSettings.profile.email")}</span> {account.email}
      </p>
      {save.isError ? <InlineError>{failure(save.error, t)}</InlineError> : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending || !trimmed}>
          {save.isPending ? t("app.saving") : t("app.save")}
        </Button>
        <span role="status" className="text-sm text-success-fg">
          {save.isSuccess ? t("accountSettings.saved") : null}
        </span>
      </div>
    </form>
  );
}

function PreferencesSection() {
  const { t } = useTranslation();
  const id = useId();
  const { choice } = useTheme();
  const save = useSavePreferences();
  const me = useMe();
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm font-medium text-fg">{t("account.language")}</legend>
        <RadioGroup
          value={currentLocale()}
          onValueChange={(value) => save.language(value as (typeof LOCALES)[number])}
        >
          {LOCALES.map((locale) => (
            <div key={locale} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-lang-${locale}`} value={locale} />
              <Label htmlFor={`${id}-lang-${locale}`} lang={locale} className="font-normal">
                {t(`account.languages.${locale}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm font-medium text-fg">{t("account.theme")}</legend>
        <RadioGroup value={choice} onValueChange={(value) => save.theme(value as ThemeChoice)}>
          {THEMES.map((theme) => (
            <div key={theme} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-theme-${theme}`} value={theme} />
              <Label htmlFor={`${id}-theme-${theme}`} className="font-normal">
                {t(`account.themes.${theme}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      {me.data?.feedback === true ? (
        // The agent's unprompted feedback suggestions (feedback-from-conversation D6).
        <div className="flex items-start justify-between gap-4 sm:col-span-2">
          <div className="flex min-w-0 flex-col gap-1">
            <Label htmlFor={`${id}-suggest-feedback`}>{t("accountSettings.suggestFeedback")}</Label>
            <p id={`${id}-suggest-feedback-help`} className="text-xs text-fg-muted text-pretty">
              {t("accountSettings.suggestFeedbackHelp")}
            </p>
          </div>
          <Switch
            id={`${id}-suggest-feedback`}
            aria-describedby={`${id}-suggest-feedback-help`}
            checked={me.data.preferences?.suggestFeedback !== false}
            disabled={save.saving}
            onCheckedChange={(on) => void save.suggestFeedback(on).catch(() => {})}
          />
        </div>
      ) : null}
      {save.error ? (
        <div className="sm:col-span-2">
          <InlineError>{t("accountSettings.preferencesNotSaved")}</InlineError>
        </div>
      ) : null}
    </div>
  );
}

/** Current password (and code when asked), then the new one; other sessions end. */
function PasswordSection() {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setResult(null);
    if (!passwordLongEnough(next)) {
      setError(t("password.tooShort"));
      return;
    }
    setBusy(true);
    try {
      const reauth = await api.api.account.reauth.$post(
        { json: { password: current, ...(needsCode ? { code } : {}) } },
        csrf(csrfToken),
      );
      const body = (await reauth.json().catch(() => null)) as { next?: string } | null;
      if (reauth.ok && body?.next === "totp") {
        setNeedsCode(true);
        setError(null);
        return;
      }
      if (!reauth.ok || body?.next !== "done") {
        setError(
          reauth.status === 429 ? t("signIn.errors.throttledLater") : t("password.wrongCurrent"),
        );
        return;
      }
      const changed = await ok(
        await api.api.account.password.$post({ json: { password: next } }, csrf(csrfToken)),
      );
      setResult(t("password.changed", { count: changed.signedOut }));
      setError(null);
      setCurrent("");
      setNext("");
      setCode("");
      setNeedsCode(false);
      void queryClient.invalidateQueries({ queryKey: accountKeys.all });
    } catch (e) {
      setError(e instanceof ApiError && e.status === 400 ? t("password.weak") : failure(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      aria-labelledby={`${id}-heading`}
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <SubHeading id={`${id}-heading`}>{t("password.title")}</SubHeading>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-current`}>{t("password.current")}</Label>
        <Input
          id={`${id}-current`}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      {needsCode ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-code`}>{t("totp.code")}</Label>
          <Input
            id={`${id}-code`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="max-w-40 font-mono tracking-widest"
          />
        </div>
      ) : null}
      <NewPasswordField value={next} onChange={setNext} />
      {error ? <InlineError>{error}</InlineError> : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy}>
          {t("password.change")}
        </Button>
        <span role="status" className="text-sm text-success-fg">
          {result}
        </span>
      </div>
    </form>
  );
}

function TotpSection({ account, secure }: { account: AccountResponse; secure: Secure }) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: accountKeys.account });

  const act = (work: () => Promise<void>) => async () => {
    setBusy(true);
    setError(null);
    try {
      await secure(work);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "last_factor"
          ? t("totp.lastFactor")
          : e instanceof ApiError && e.status === 400
            ? t("signIn.errors.invalidCode")
            : failure(e, t),
      );
    } finally {
      setBusy(false);
    }
  };

  const start = act(async () => {
    setStatus(null);
    setSetup(await ok(await api.api.account.totp.$post(undefined, csrf(csrfToken))));
  });
  const confirm = (code: string) =>
    act(async () => {
      await done(await api.api.account.totp.verify.$post({ json: { code } }, csrf(csrfToken)));
      setSetup(null);
      setStatus(t("totp.enabled"));
      await refresh();
    })();
  const remove = act(async () => {
    await done(await api.api.account.totp.$delete(undefined, csrf(csrfToken)));
    setStatus(t("totp.removed"));
    await refresh();
  });

  return (
    <div className="flex max-w-xl flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SubHeading id={`${id}-heading`}>{t("totp.title")}</SubHeading>
        <Badge variant={account.security.totp ? "secondary" : "outline"}>
          {account.security.totp ? t("totp.on") : t("totp.off")}
        </Badge>
      </div>
      <p className="text-sm leading-relaxed text-fg-secondary">
        {account.security.mfaRequired ? t("totp.required") : t("totp.lead")}
      </p>
      {setup ? (
        <TotpSetup
          secret={setup.secret}
          uri={setup.uri}
          pending={busy}
          error={error}
          onConfirm={(code) => void confirm(code)}
          onCancel={() => setSetup(null)}
        />
      ) : (
        <>
          {error ? <InlineError>{error}</InlineError> : null}
          <div>
            {account.security.totp ? (
              <Button type="button" variant="outline" disabled={busy} onClick={() => void remove()}>
                {t("totp.remove")}
              </Button>
            ) : (
              <Button type="button" disabled={busy} onClick={() => void start()}>
                {t("totp.setUp")}
              </Button>
            )}
          </div>
        </>
      )}
      <p role="status" className="text-sm text-success-fg">
        {status}
      </p>
    </div>
  );
}

function PasskeysSection({ account, secure }: { account: AccountResponse; secure: Secure }) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await secure(work);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "last_factor"
          ? t("passkeys.lastFactor")
          : e instanceof ApiError
            ? e.message
            : t("passkeys.cancelled"),
      );
    } finally {
      setBusy(false);
    }
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: accountKeys.account });

  // After a step-up the ceremony waits for the person's click (#354).
  const adder = usePasskeyAdd();
  const add = () => {
    setStatus(null);
    const run = adder.action({
      options: async () =>
        (await ok(await api.api.account.passkeys.options.$post(undefined, csrf(csrfToken))))
          .options,
      register: async (options) => {
        const credential = await createPasskey(options);
        await done(await api.api.account.passkeys.$post({ json: { credential } }, csrf(csrfToken)));
      },
    });
    return act(async () => {
      if (await run()) {
        setStatus(t("passkeys.added"));
      }
      await refresh();
    });
  };
  const remove = (passkey: Passkey) => {
    adder.clear();
    return act(async () => {
      await done(
        await api.api.account.passkeys[":id"].$delete(
          { param: { id: passkey.id } },
          csrf(csrfToken),
        ),
      );
      setStatus(t("passkeys.removed", { name: passkeyName(passkey, t) }));
      await refresh();
    });
  };
  // Listing needs a fresh re-authentication: any token-bound call asks for one.
  const reveal = () =>
    act(async () => {
      await ok(await api.api.account.passkeys.options.$post(undefined, csrf(csrfToken)));
      await refresh();
    });

  return (
    <PasskeysPanel
      heading={<SubHeading id={`${id}-heading`}>{t("passkeys.title")}</SubHeading>}
      lead={t("passkeys.lead")}
      passkeys={account.security.passkeys}
      busy={busy}
      error={error}
      status={status}
      addReady={adder.ready}
      onReveal={() => void reveal()}
      onAdd={() => void add()}
      onRemove={(passkey) => void remove(passkey)}
    />
  );
}

/** The native and MCP clients this user connected (native-device-and-cimd, D4). */
function ConnectedAppsSection() {
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-fg-secondary">{t("connectedApps.lead")}</p>
      <ConnectedApps
        queryKey={accountKeys.connectedApps}
        load={async () => ok(await api.api.account["connected-apps"].$get())}
        revoke={async (grantId) =>
          done(
            await api.api.account["connected-apps"][":grantId"].$delete(
              { param: { grantId } },
              csrf(csrfToken),
            ),
          )
        }
        empty={t("connectedApps.empty")}
      />
    </div>
  );
}

function SessionsSection({ secure }: { secure: Secure }) {
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const sessions = useAccountSessions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = async (work: () => Promise<{ revoked: number }>) => {
    setBusy(true);
    setError(null);
    try {
      await secure(async () => {
        const { revoked } = await work();
        setStatus(t("sessions.revoked", { count: revoked }));
        await queryClient.invalidateQueries({ queryKey: accountKeys.sessions });
      });
    } catch (e) {
      setError(failure(e, t));
    } finally {
      setBusy(false);
    }
  };

  if (sessions.isPending) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (sessions.isError) {
    return <Notice tone="danger">{failure(sessions.error, t)}</Notice>;
  }
  const others = sessions.data.filter((s) => !s.current);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-fg-secondary">{t("sessions.lead")}</p>
      <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
        {sessions.data.map((session) => (
          <li key={session.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
            <Monitor aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2 text-sm text-fg">
                {session.device ?? t("sessions.unknownDevice")}
                {session.current ? (
                  <Badge variant="secondary">{t("sessions.current")}</Badge>
                ) : null}
              </span>
              <span className="text-xs text-fg-muted">
                {[
                  session.network ? t("sessions.network", { network: session.network }) : null,
                  t("sessions.signedIn", { date: formatDateTime(session.signedInAt) }),
                  t("sessions.lastSeen", { date: formatDateTime(session.lastSeenAt) }),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            {session.current ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                aria-label={t("sessions.signOutNamed", {
                  device: session.device ?? t("sessions.unknownDevice"),
                })}
                onClick={() =>
                  void act(async () =>
                    ok(
                      await api.api.account.sessions[":id"].$delete(
                        { param: { id: session.id } },
                        csrf(csrfToken),
                      ),
                    ),
                  )
                }
              >
                {t("sessions.signOut")}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {others.length > 0 ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void act(async () =>
                ok(
                  await api.api.account.sessions["revoke-others"].$post(undefined, csrf(csrfToken)),
                ),
              )
            }
          >
            {t("sessions.signOutOthers")}
          </Button>
        </div>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      <p role="status" className="text-sm text-success-fg">
        {status}
      </p>
    </div>
  );
}
